import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, Copy } from 'lucide-react';
import moment from 'moment';
import toast from 'react-hot-toast';

const BookingSuccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [copied, setCopied] = useState(false);

    const { bookingId, successData } = location.state || {};

    useEffect(() => {
        // Prevent back navigation
        const handlePopState = (e) => {
            e.preventDefault();
            navigate('/booking', { replace: true });
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate]);

    const formatBookingId = (id) => {
        if (!id) return '--';
        const idStr = String(id);
        return idStr.slice(-6); // Show last 6 digits like mobile app
    };

    const copyBookingId = (id) => {
        if (!id) return;
        navigator.clipboard.writeText(String(id));
        setCopied(true);
        toast.success('Booking ID copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleViewBooking = () => {
        // Reset navigation stack and go to booking page
        window.history.replaceState(null, '', '/bookingsDetail');
        navigate('/bookingsDetail', { replace: true });
    };

    const handleBackToHome = () => {
        // Reset navigation stack and go to home page
        window.history.replaceState(null, '', '/booking');
        navigate('/booking', { replace: true });
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-2xl mx-auto px-4 py-8">
                {/* Success Icon */}
                <div className="flex flex-col items-center mb-8">
                    <div className="bg-red-500 rounded-full p-6 mb-4">
                        <Check size={80} className="text-white" strokeWidth={3} />
                    </div>
                    <h1 className="text-3xl font-bold text-black text-center tracking-wide">
                        Yay! Booking Confirmed
                    </h1>
                    <p className="text-slate-400 text-center font-semibold mt-2">
                        See you on court soon!
                    </p>
                </div>

                {/* Booking Details Card */}
                <div className="relative bg-white border border-slate-100 rounded-2xl shadow-lg p-6 mb-8">
                    {/* Decorative circles on sides */}
                    <div className="absolute -left-4 bg-white border-r border-slate-200 rounded-full w-8 h-8" style={{ top: '45%' }} />
                    <div className="absolute -right-4 bg-white border-l border-slate-200 rounded-full w-8 h-8" style={{ top: '45%' }} />

                    <div className="space-y-4">
                        {/* Booking ID */}
                        <div className="flex items-start justify-between">
                            <span className="text-black font-semibold text-lg">Booking ID</span>
                            <div className="flex items-center gap-2">
                                <span className="text-red-500 font-semibold">
                                    #{formatBookingId(successData?.bookingDetails?.booking_id || bookingId)}
                                </span>
                                <button
                                    onClick={() => copyBookingId(successData?.bookingDetails?.booking_id || bookingId)}
                                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                                >
                                    {copied ? (
                                        <Check size={18} className="text-green-500" />
                                    ) : (
                                        <Copy size={18} className="text-red-500" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Booking Date */}
                        <div className="flex items-center justify-between">
                            <span className="text-black font-semibold text-lg">Booking Date</span>
                            <span className="text-black font-semibold">
                                {successData?.bookingDate
                                    ? moment(successData.bookingDate, 'YYYY-MM-DD').format('dddd, DD MMM YYYY')
                                    : moment().format('dddd, DD MMM YYYY')}
                            </span>
                        </div>

                        {/* Slot Time */}
                        <div className="flex items-center justify-between">
                            <span className="text-black font-semibold text-lg">Slot Time</span>
                            <span className="text-black font-semibold">
                                {successData?.slotTime || successData?.bookingDetails?.time_slot || '--'}
                            </span>
                        </div>

                        {/* Payment Status */}
                        <div className="flex items-center justify-between">
                            <span className="text-black font-semibold text-lg">Payment Status</span>
                            <span className="text-green-600 font-semibold">
                                {successData?.paymentStatus || 'Success'}
                            </span>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-dashed border-slate-300 my-4"></div>

                        {/* Amount Paid */}
                        <div className="flex items-center justify-between">
                            <span className="text-black font-semibold text-lg">Amount Paid</span>
                            <span className="text-black font-bold text-xl">
                                ₹ {successData?.amountPaid?.toFixed(2) || '0.00'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    <button
                        onClick={handleViewBooking}
                        className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-lg transition-colors shadow-md"
                    >
                        View Booking
                    </button>
                    <button
                        onClick={handleBackToHome}
                        className="w-full bg-slate-100 hover:bg-slate-200 text-red-500 font-bold py-4 rounded-lg transition-colors"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BookingSuccess;
